import React, { useMemo } from "react";
import { useSelector } from 'react-redux';
import { Field, FormSection, getFormValues } from "redux-form";
import { ReduxControls, ErrorBanner } from "rhombus-react";
import { SOCIAL_AD_TYPES, ASSET_TYPES } from "utils/constants";
import { formatBytes, noop } from "utils/common";
import { UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE } from "social/constants/common";
import { REDDIT_CTAS } from "social/constants/CTA";

import {
	dynamicNormalizeOptionToString,
	dynamicMapStringToOption,
	prettifyMessages,
	mapStringToOption, isRequiredAssetField
} from "social/utils";
import { assetType } from "social/BuildMethodRadio";
import { isDynamic } from "social/DynamicCreativeToggle";
import DynamicAssetSelect from "social/DynamicAssetSelect";
import DynamicSelect from "social/DynamicSelect";
import DynamicInput from "social/DynamicInput";
import RedditDisclaimer from "social/RedditDisclaimer";
import { REDDIT_IMAGE_CONSTRAINTS } from "social/validators/reddit";

import { CREATIVE_NAME_MAX_LENGTH } from "widgets/forms/validators/Creatives";
import { imposiumProject } from "social/ImposiumProjectSelector";
import {
	imposiumCompositionWithoutProjectName
} from "social/ImposiumCompSelector";
import { getErrorMessagesByAdType } from "selectors/Social2";
import { SOCIAL_WIZARD } from "forms/list";

const {
	MAX_LENGTH_LANDING_PAGE,
	MAX_LENGTH_HEADLINE,
	MAX_IMAGE_BYTES,
	MAX_LENGTH_DISPLAY_LINK,
	IMAGE_ACCEPT,
	MAX_THUMBNAIL_BYTES,
	THUMBNAIL_ACCEPT,
} = REDDIT_IMAGE_CONSTRAINTS;

const REDDIT_THUMBNAIL_REQUIREMENTS = [
	{
		name: "File Size:",
		description: `<= ${formatBytes(MAX_THUMBNAIL_BYTES)}`,
	},
	{
		name: "File Types:",
		description: THUMBNAIL_ACCEPT,
	}
];

export const REDDIT_IMAGE_FIELDS = {
	isDynamic,
	assetType,
	imposiumProject,
	imposiumComposition: imposiumCompositionWithoutProjectName,
	image: {
		name: "image",
		label: "Image",
		assetType: ASSET_TYPES.IMAGE,
		accept: IMAGE_ACCEPT,
		component: DynamicAssetSelect,
		defaultDynVarName: "product",
		required: true,
		className: "full-width",
		requirements: [
			{
				name: "File Size",
				description: `${formatBytes(MAX_IMAGE_BYTES)} Maximum`,
			},
			{
				name: "File Types",
				description: IMAGE_ACCEPT,
			},
		],
		tip: {
			direction: "top",
			description: "",
		},
	},
	thumbnail: {
		name: "thumbnail",
		label: "Thumbnail",
		className: "full-width",
		assetType: ASSET_TYPES.IMAGE,
		component: DynamicAssetSelect,
		defaultDynVarName: "thumbnail",
		requirements: REDDIT_THUMBNAIL_REQUIREMENTS,
		accept: THUMBNAIL_ACCEPT,
		tip: {
			direction: "top",
			description: "",
		},
	},
	name: {
		name: "name",
		label: "Creative Name",
		placeholder: "Enter a creative name",
		component: ReduxControls.Input,
		required: true,
		className: "full-width",
		charMax: CREATIVE_NAME_MAX_LENGTH,
		showCharCount: true,
		showPristineErrors: true,
	},
	headline: {
		name: "headline",
		label: "Headline",
		placeholder: "Enter a short headline",
		component: DynamicInput,
		defaultDynVarName: "headline",
		className: "full-width",
		tip: {
			direction: "right",
			description: "May get truncated after 300 characters. Recommended: ≤ 100 characters. Safer limit: ≤ 80 characters.",
		},
		charMax: MAX_LENGTH_HEADLINE,
	},
	disclaimer: {
		name: "disclaimerEnabled",
		component: RedditDisclaimer,
	},
	landingPage: {
		name: "landingPage",
		label: "Landing Page",
		placeholder: "https://www.example.com",
		component: DynamicInput,
		defaultDynVarName: "clickTag1",
		required: true,
		className: "full-width",
		charMax: MAX_LENGTH_LANDING_PAGE,
		tip: {
			direction: "right",
			description: "May get truncated after 268 characters",
		},
	},
	displayLink: {
		name: "displayLink",
		label: "Display Link",
		className: "half-width",
		placeholder: "Enter link text to be displayed",
		component: DynamicInput,
		defaultDynVarName: "display_link",
		charMax: MAX_LENGTH_DISPLAY_LINK,
		tip: {
			direction: "right",
			description: "May get truncated after 100 characters. Must match your destination URL's domain.",
		},
	},
	cta: {
		name: "cta",
		label: "CTA",
		className: "half-width",
		placeholder: "Select Call to Action Text",
		component: DynamicSelect,
		defaultDynVarName: "cta",
		options: REDDIT_CTAS.map(mapStringToOption),
		normalize: dynamicNormalizeOptionToString,
		format: dynamicMapStringToOption,
		isClearable: true,
		menuPlacement: "top",
		onMenuScrollToBottom: noop,
		variableNameInputTip: "Edit variable name",
	},
};

export default function RedditImage(props) {
	const { navigationActions, libraryId, isViewOnly, showPristineErrors, doVersionsExist } = props;

	const formValues = useSelector(getFormValues(SOCIAL_WIZARD)) || {};
	const { assetType } = formValues[SOCIAL_AD_TYPES.REDDIT_IMAGE] || {};

	const errorMessages = useSelector(getErrorMessagesByAdType(SOCIAL_AD_TYPES.REDDIT_IMAGE, showPristineErrors));
	const prettyErrorMessages = useMemo(() => (
		prettifyMessages(errorMessages, REDDIT_IMAGE_FIELDS)
	), [errorMessages]);

	const hasErrors = prettyErrorMessages.length;
	const errorDisplay = hasErrors ? <ErrorBanner title="Please review the warnings below" messages={prettyErrorMessages} /> : null;

	const $fields = Object.values(REDDIT_IMAGE_FIELDS).map((fieldProps) => {
		const extraProps = {};
		extraProps.disabled = isViewOnly;

		if (fieldProps.name === "image" || fieldProps.name === "thumbnail") {
			extraProps.navigationActions = navigationActions;
			extraProps.libraryId = libraryId;
			extraProps.creativeAssetType = assetType;
			if (fieldProps.name === "image") {
				extraProps.required = isRequiredAssetField(assetType);
			}
		}

		if (fieldProps.name === "assetType") {
			extraProps.isAdStudioSupported = false;
		}

		// If versions exist besides default, then dynamic toggle should be disabled
		if (doVersionsExist) {
			extraProps.dynamicToggleDisabled = true;
			extraProps.variableNameInputDisabled = true;
			if (fieldProps.name !== "cta") {
				extraProps.variableNameInputTip = UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE;
			}
		}

		if (extraProps.disabled && fieldProps.placeholder) {
			// We set this on extraProps, because we don't want to mutate the fieldProps consts
			extraProps.placeholder = "";
		}

		extraProps.showPristineErrors = showPristineErrors;

		return <Field key={fieldProps.name} {...fieldProps} {...extraProps} />;
	});

	return (
		<div className="form-container">
			<FormSection name={SOCIAL_AD_TYPES.REDDIT_IMAGE}>
				{errorDisplay}
				<div className="fields">
					{$fields}
				</div>
			</FormSection>
		</div>
	);
}

RedditImage.displayName = "RedditImageForm";
